import { useEffect } from "react";

/**
 * How far a page's footer controls must stay from each side of the window so
 * the assistant's avatar never sits on top of them.
 *
 * Published as CSS custom properties on <html> by the overlay, read by footers
 * that put controls in a bottom corner (e.g. a pagination bar):
 *   padding-right: max(1.5rem, var(--mochi-clear-right, 0px));
 * Physical sides, not logical: the avatar's corner is physical too, so an RTL
 * page keeps clear of it the same way.
 */
export const MOCHI_CLEAR_RIGHT = "--mochi-clear-right";
export const MOCHI_CLEAR_LEFT = "--mochi-clear-left";

/** The strip along the bottom of the window that footers live in. */
export const FOOTER_BAND_PX = 96;
/** Breathing room between the avatar and the nearest control. */
const GAP_PX = 8;

export interface Clearance {
  right: number;
  left: number;
}

const NONE: Clearance = { right: 0, left: 0 };

/** Pure: the clearance an avatar at `rect` needs, in a `width`×`height` window. */
export function computeMochiClearance(
  rect: { left: number; right: number; top: number; bottom: number },
  width: number,
  height: number,
): Clearance {
  // Only an avatar that reaches into the footer band can cover a footer.
  if (rect.bottom <= height - FOOTER_BAND_PX || rect.right <= rect.left) return NONE;
  const centre = (rect.left + rect.right) / 2;
  return centre >= width / 2
    ? { right: Math.max(0, Math.ceil(width - rect.left + GAP_PX)), left: 0 }
    : { right: 0, left: Math.max(0, Math.ceil(rect.right + GAP_PX)) };
}

function publish(c: Clearance) {
  const style = document.documentElement.style;
  style.setProperty(MOCHI_CLEAR_RIGHT, `${c.right}px`);
  style.setProperty(MOCHI_CLEAR_LEFT, `${c.left}px`);
}

/**
 * Keep the clearance variables in step with the avatar element (pass it via a
 * callback ref, so the effect sees it mount and unmount): its size
 * (avatar size setting, lazy scene swapping in for the fallback), the window
 * size, and wherever a drag left it (`positionKey`). Cleared when the avatar
 * unmounts, so a hidden assistant reserves no space.
 */
export function useMochiClearance(el: HTMLElement | null, positionKey: unknown) {
  useEffect(() => {
    if (!el) {
      publish(NONE);
      return;
    }
    const update = () =>
      publish(computeMochiClearance(el.getBoundingClientRect(), window.innerWidth, window.innerHeight));
    update();
    window.addEventListener("resize", update);
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => {
      window.removeEventListener("resize", update);
      ro?.disconnect();
      publish(NONE);
    };
  }, [el, positionKey]);
}
